import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * BottomDrawer — mobile bottom sheet primitive.
 * Slides up from the bottom, closes on backdrop click, swipe-down, or ESC.
 *
 * Props:
 *   isOpen       {boolean}
 *   onClose      {function}
 *   title        {string|ReactNode}   — shown in the drag-handle header
 *   children     {ReactNode}
 *   maxHeight    {string}             — CSS value, default '90vh'
 */
export default function BottomDrawer({ isOpen, onClose, title, children, maxHeight = '90vh' }) {
    const [visible, setVisible] = useState(false);
    const [mounted, setMounted] = useState(false);
    const sheetRef = useRef(null);
    // touch-swipe tracking
    const touchStartY = useRef(null);

    // Mount → next frame → set visible (triggers CSS transition)
    useEffect(() => {
        if (isOpen) {
            setMounted(true);
            requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
        } else {
            setVisible(false);
            const t = setTimeout(() => setMounted(false), 320);
            return () => clearTimeout(t);
        }
    }, [isOpen]);

    // Close on ESC
    useEffect(() => {
        if (!isOpen) return;
        const handler = e => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    // Lock body scroll while open
    useEffect(() => {
        if (isOpen) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = prev; };
        }
    }, [isOpen]);

    const handleTouchStart = e => { touchStartY.current = e.touches[0].clientY; };
    const handleTouchEnd = e => {
        if (touchStartY.current === null) return;
        const delta = e.changedTouches[0].clientY - touchStartY.current;
        if (delta > 60) onClose();
        touchStartY.current = null;
    };

    if (!mounted) return null;

    return createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050 }}>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,.45)',
                    opacity: visible ? 1 : 0,
                    transition: 'opacity .3s ease',
                }}
            />

            {/* Sheet */}
            <div
                ref={sheetRef}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: '#fff',
                    borderRadius: '20px 20px 0 0',
                    maxHeight,
                    display: 'flex', flexDirection: 'column',
                    transform: visible ? 'translateY(0)' : 'translateY(100%)',
                    transition: 'transform .32s cubic-bezier(.32,1,.23,1)',
                    boxShadow: '0 -4px 32px rgba(0,0,0,.12)',
                    overflowY: 'auto',
                }}
            >
                {/* Drag handle */}
                <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 4 }}>
                    <div style={{ width: 36, height: 4, borderRadius: 2, background: '#E2E8F0' }} />
                </div>

                {/* Header */}
                {title != null && (
                    <div style={{
                        flexShrink: 0, display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', padding: '12px 20px 16px',
                    }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{title}</span>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                width: 30, height: 30, borderRadius: '50%', border: 'none',
                                background: '#F1F5F9', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <X size={14} color="#64748B" />
                        </button>
                    </div>
                )}

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 32px' }}>
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
}
